import type {ParserT} from '@freckle/parser'
import {stringEnum} from '@freckle/parser'
import type {TranslateT} from '@freckle/educator-entities/ts/common/helpers/translate'

export type IdentityProviderT = 'clever' | 'csv' | 'class-link' | 'renaissance'
export const IdentityProvider = {
  all: () => [
    IdentityProvider.Clever,
    IdentityProvider.CSV,
    IdentityProvider.ClassLink,
    IdentityProvider.Renaissance,
  ],
  Clever: 'clever' as IdentityProviderT,
  CSV: 'csv' as IdentityProviderT,
  ClassLink: 'class-link' as IdentityProviderT,
  Renaissance: 'renaissance' as IdentityProviderT,
}

type IdpEntity = Inexact<{
  identityProvider: IdentityProviderT | undefined | null
  idpManaged: boolean
}>

export const isIdpManaged = (entity: Pick<IdpEntity, 'idpManaged'>) => entity.idpManaged

export const isManagedByClever = <Others>(
  entity: Inexact<{identityProvider?: 'clever' | Others}>,
) => entity.identityProvider === IdentityProvider.Clever

export const isManagedByCSV = <Others>(entity: Inexact<{identityProvider?: 'csv' | Others}>) =>
  entity.identityProvider === IdentityProvider.CSV

export const isManagedByClassLink = <Others>(
  entity: Inexact<{identityProvider?: 'class-link' | Others}>,
) => entity.identityProvider === IdentityProvider.ClassLink

export const isManagedByRenaissance = <Others>(
  entity: Inexact<{identityProvider?: 'renaissance' | Others}>,
) => entity.identityProvider === IdentityProvider.Renaissance

export type ManagedCountT = {
  classlink: number
  clever: number
  renaissance: number
  upload: number
}

export const combineManagedCounts = (a: ManagedCountT, b: ManagedCountT): ManagedCountT => ({
  classlink: a.classlink + b.classlink,
  clever: a.clever + b.clever,
  renaissance: a.renaissance + b.renaissance,
  upload: a.upload + b.upload,
})

const singletonWhenAboveZero = (idp: IdentityProviderT, n: number): Array<IdentityProviderT> =>
  n > 0 ? [idp] : []

export const identityProvidersWithCount = ({
  classlink,
  clever,
  renaissance,
  upload,
}: Inexact<ManagedCountT>): Array<IdentityProviderT> => [
  ...singletonWhenAboveZero(IdentityProvider.Clever, clever),
  ...singletonWhenAboveZero(IdentityProvider.Renaissance, renaissance),
  ...singletonWhenAboveZero(IdentityProvider.ClassLink, classlink),
  ...singletonWhenAboveZero(IdentityProvider.CSV, upload),
]

export const parse = (s: string): IdentityProviderT | undefined | null =>
  IdentityProvider.all().find(role => s === role) ?? null

/**
 * Translate an identity provider into its display label.
 *
 * These translations are ICU `select` messages keyed on the `idp` value. ICU
 * `select` selectors must be valid identifiers (letters, digits, and underscores
 * only) — hyphens are illegal and cause the whole message to fail to parse, which
 * `i18next-icu` then silently renders as the raw ICU source string. Our
 * `class-link` provider value contains a hyphen, so we normalize hyphens to
 * underscores here before interpolation. The corresponding Locize selectors must
 * use the matching `class_link` key.
 *
 * `idp` is typed as `string` rather than `IdentityProviderT` so it can also serve
 * the school dashboard's wider `DashboardIdentityProviderT` (which adds `manual`
 * and likewise contains the hyphenated `class-link`). `key` defaults to the common
 * `DISPLAY_IDP` label but can be any ICU-select translation keyed on `idp`.
 */
export const getIdpLabel = (
  t: TranslateT,
  idp: string | undefined | null,
  key: string = 'DISPLAY_IDP',
): string => t(key, {idp: idp?.replace(/-/g, '_')})

export const parser: ParserT<IdentityProviderT | undefined | null> = stringEnum(
  'IdentityProviderT',
  parse,
)
